import "./utilities_namespace.js";
import "./publishing_domain.js";
import "./locale.js";
import "../../utilities/event_originated_from.js";

window.Sparkle ??= {};
window.Sparkle.Embed ??= {};
window.Sparkle.Embed.Utilities ??= {};

Sparkle.Embed.Utilities.decorateEmbeddedHrefs = function (container, options) {
  Sparkle.Embed.Utilities.modifyHrefs(container, Sparkle.Embed.Utilities.decorateEmbeddedUrl, options);
};

// Modern browsers prevent the full referrer path from being transmitted cross-origin, so we inject an embedded at param into all
// links to content on the Sparkle server so we know what embedding endpoint it came from.
Sparkle.Embed.Utilities.injectHrefsWithEmbeddedAt = function (container) {
  Sparkle.Embed.Utilities.modifyHrefs(
    container,
    Sparkle.Embed.Utilities.injectUrlWithEmbeddedAt.bind(Sparkle.Embed.Utilities),
  );
};

// @param container   CSS selector, element, or list of elements to listen on.
// @param decorator   Function that modifies the url. Accepts a url string and returns the modified url string.
Sparkle.Embed.Utilities.modifyHrefs = function (container, decorator, ...decoratorArgs) {
  // Use 'focusin' so a link reached by keyboard, or copied before any click, is decorated
  // Use 'click' and other click-related events because desktop Safari doesn't focus all elements, e.g. slot booking links
  const types = ["focusin", "click", "contextmenu", "auxclick"];
  const watchedShadowRoots = new WeakSet();
  const decoratedEvents = new WeakSet();

  for (const element of containerElements(container)) {
    for (const type of types) {
      element.addEventListener(type, decorateLink);
    }
  }

  // A focus move between two links in one shadow root stops at that root, so each root on the path listens itself.
  function watchShadowRoots(event) {
    for (const target of event.composedPath()) {
      if (target instanceof ShadowRoot && !watchedShadowRoots.has(target)) {
        watchedShadowRoots.add(target);
        target.addEventListener("focusin", decorateLink);
      }
    }
  }

  function decorateLink(event) {
    if (decoratedEvents.has(event)) {
      return;
    }
    decoratedEvents.add(event);
    watchShadowRoots(event);

    // Check for `<a>` tag origin using Shadow DOM-aware function. If the click didn't come from an `<a>`, best not to mess with it.
    const element = Sparkle.Utilities.eventOriginElement(event, "a");
    const href = element && element.getAttribute("href");

    if (href) {
      element.setAttribute("href", decorator(href, ...decoratorArgs));
    }
  }
};

function containerElements(container) {
  if (typeof container === "string") {
    return document.querySelectorAll(container);
  }
  if (container instanceof Element) {
    return [container];
  }

  return Array.from(container ?? []);
}

// Applies standard decoration for an embedded url
Sparkle.Embed.Utilities.decorateEmbeddedUrl = function (url, options = {}) {
  // If we're on the Sparkle host, don't perform any mapping or we will create offsite links when we intend to remain on the Sparkle server
  // NOTE: There are links to sparkle resources that we DO want to create offsite links to the host CMS for, namely those resources that we
  // don't handle for the client, e.g. Locations for Atrium. That is more of an edge case than having all links except those be wrong, so
  // we're defaulting to not mapping if on the Sparkle domain.
  if (Sparkle.Embed.Utilities.isSparkleDomain()) {
    return url;
  }

  url = Sparkle.Embed.Utilities.mapLocalizedUrl(url, options.urlMap); // Map the url if it matches a map entry
  url = Sparkle.Embed.Utilities.injectUrlWithEmbeddedAt(url); // Inject embedded at after mapping, because urls may be mapped and no longer need an embedded_at param
  url = Sparkle.Embed.Utilities.injectUrlWithLocale(url); // Keep the visitor's language across embedded navigation (after mapping, so host CMS links carry it too)
  url = Sparkle.Embed.Utilities.modifyUrlParams(url, options.urlParams); // Include optional url params on all links

  return url;
};

// If the url will be handled by the Sparkle server we need to know which embedding endpoint it came from, so we add the embedded_at param
Sparkle.Embed.Utilities.injectUrlWithEmbeddedAt = function (url) {
  // Don't add `embedded_at` if we're already on the Sparkle domain since this is not an embedding endpoint
  if (Sparkle.Embed.Utilities.isSparkleDomain()) {
    return url;
  }

  // Don't add embedded_at to urls for non-sparkle domains because they won't use the param
  if (!Sparkle.Embed.Utilities.isSparkleDomain(url)) {
    return url;
  }

  return Sparkle.Embed.Utilities.modifyUrlParams(url, { embedded_at: window.location.href });
};

// Modifies the params of the given url, adding missing params, and removing params whose value is blank
Sparkle.Embed.Utilities.modifyUrlParams = function (url, params) {
  if (!params) {
    return url;
  }

  // Create a URL object
  const urlObj = new URL(url, window.location);

  if (urlObj.protocol !== "http:" && urlObj.protocol !== "https:") {
    return url; // Only modify http and https URLs
  }

  const searchParams = urlObj.searchParams; // Get existing search params

  if (params instanceof URLSearchParams) {
    // All is well, we will just use the params as is
  } else if (typeof params === "object") {
    // Use jQuery to serialize nested object params into query string
    const urlParamString = jQuery.param(params);
    params = new URLSearchParams(urlParamString);
  } else if (typeof params === "string") {
    params = new URLSearchParams(params);
  } else {
    throw new TypeError("Params should be either a plain object or URLSearchParams");
  }

  // Delete existing params with the same key
  params.forEach((value, key) => {
    searchParams.delete(key);
  });

  // Add the new param if it is not blank (keeps duplicates in the same order)
  params.forEach((value, key) => {
    if (value !== "") {
      searchParams.append(key, value);
    }
  });

  // Return the updated URL as a string
  return urlObj.toString();
};

// Maps `url` like `mapUrl`, also when it carries a locale path prefix the map's bare patterns do not
// name. A mapped url is addressed the way the host takes a locale (`localizeHostUrl`): to the locale
// the url names (`urlLocale`), else to the page's locale, since a Sparkle url built without one (a
// slot's booking link) still belongs to the page it appears on.
// "/es/physicians/jane-doe" → "https://host.org/physician-locator/jane-doe?locale=es". An unmapped url
// is returned as given, prefix included.
Sparkle.Embed.Utilities.mapLocalizedUrl = function (url, map) {
  const locale = Sparkle.Embed.Utilities.urlLocale(url);
  if (!locale) {
    const mapped = Sparkle.Embed.Utilities.mapUrl(url, map);

    return mapped === url
      ? url
      : Sparkle.Embed.Utilities.localizeHostUrl(mapped, Sparkle.Embed.Utilities.currentLocale());
  }

  const bare = Sparkle.Embed.Utilities.stripLocalePrefix(url);
  const mapped = Sparkle.Embed.Utilities.mapUrl(bare, map);

  return mapped === bare ? url : Sparkle.Embed.Utilities.localizeHostUrl(mapped, locale);
};

// Iterate the map, returning the mapped url if any of the patterns in the map apply. Else, returns the original url.
// Key/value pairs of Matchers and Targets to map links to. Matcher can be a String or a RegExp. A String Matcher can
// include capture groups, e.g. "some/url/<my-capture>", whereas a RegExp matcher should follow JS RegExp syntax for
// capture groups. Target can be either a String or a Function. A String target can make use of capture groups from the
// Matcher, e.g. "some/mapped/url/<my-capture>". A Function target receives the matching portion of the url and should
// return a string that will replace the match.
// Also accepts a filter function which receives the url and returns a boolean signaling whether to map the url or not.
// The default filter only allows modifications to urls without an extension.
Sparkle.Embed.Utilities.mapUrl = function (url, map, filter = Sparkle.Embed.Utilities.isHTMLUrl) {
  if (!map) {
    return url;
  }
  if (filter && !filter(url)) {
    return url;
  }

  const segmentDelimiters = "/?#:";
  var newURL = url;

  // Support Map or Object as `map`
  if (!(map instanceof Map)) {
    var newMap = new Map();
    jQuery.each(map, function (k, v) {
      newMap.set(k, v);
    });
    map = newMap;
  }

  for (var [matcher, target] of map.entries()) {
    if (typeof matcher == "string") {
      matcher = buildMatcher(matcher);
    }

    if (typeof target == "string") {
      target = buildTarget(target);
    }

    if (matcher.test(url)) {
      newURL = url.replace(matcher, target);
      break;
    }
  }

  return newURL;

  function buildMatcher(matcher) {
    return new RegExp(escapeRegExp(matcher).replace(/<([^>]+)>/g, matcherCaptureGroup));
  }

  function buildTarget(target) {
    return target.replace(/<([^>]+)>/g, targetCaptureGroup);
  }

  function matcherCaptureGroup(_, group) {
    return `(?<${sanitizeCaptureGroup(group)}>[^${new RegExp(escapeRegExp(segmentDelimiters))}]+)`;
  }

  function targetCaptureGroup(_, group) {
    return `$<${sanitizeCaptureGroup(group)}>`;
  }

  function sanitizeCaptureGroup(match) {
    return match.replace(/[-]/, "_");
  }

  // SOURCE: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
  function escapeRegExp(string) {
    return string.replace(/[/.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
  }
};

Sparkle.Embed.Utilities.isHTMLUrl = function (url) {
  try {
    return !new URL(url, window.location).pathname.includes(".");
  } catch {
    return false;
  }
};
